import { useEffect, useMemo, useState } from "react";
import {
  Rocket,
  ShieldCheck,
  Car,
  Wallet,
  CarFront,
  MessageCircle,
  Lock,
  Scale,
  Headphones,
  MapPin,
  Search,
  HelpCircle,
  ChevronRight,
  Globe,
  Menu,
  Sparkles,
  BadgeIndianRupee,
  Briefcase,
  Smartphone,
  ArrowRight,
} from "lucide-react";
import FAQForum from "@/components/FAQForum";
import { faqCategories, type FAQCategory } from "@/data/faqs";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const iconMap = {
  Rocket,
  ShieldCheck,
  Car,
  Wallet,
  CarFront,
  MessageCircle,
  Lock,
  Scale,
  Headphones,
  MapPin,
  Sparkles,
  BadgeIndianRupee,
  Briefcase,
} as const;

const DAILY_COMMUTES_CATEGORY_ID = "daily-commutes";

const normalizeSearchText = (text: string) => text.toLowerCase().replace(/-/g, " ");

/** Commute-specific searches should show the full Daily Commute Carpool category (9 FAQs), not a partial subset. */
const isDailyCommuteSearch = (q: string) => {
  if (!q) return false;
  return (
    q.includes("daily commute") ||
    q.includes("daily commute carpool") ||
    q.includes("daily commute carpool") ||
    q.includes("daily commute carpool") ||
    (q.includes("daily") && q.includes("commute")) ||
    q === "commute" ||
    q === "commutes" ||
    q === "commute carpool" ||
    q === "commutes carpool"
  );
};

const categoryMatchesSearch = (cat: FAQCategory, q: string) => {
  const haystack = normalizeSearchText(`${cat.id} ${cat.title} ${cat.description}`);
  return haystack.includes(q);
};

const faqMatchesSearch = (cat: FAQCategory, q: string) =>
  cat.faqs.filter((f) => {
    const haystack = normalizeSearchText(`${f.question} ${f.answer}`);
    return haystack.includes(q);
  });

const filterFaqCategories = (query: string): FAQCategory[] => {
  const q = normalizeSearchText(query).trim();
  if (!q) return faqCategories;

  const dailyCommutes = faqCategories.find((c) => c.id === DAILY_COMMUTES_CATEGORY_ID);
  if (dailyCommutes && isDailyCommuteSearch(q)) {
    return [dailyCommutes];
  }

  return faqCategories
    .map((cat) => {
      if (categoryMatchesSearch(cat, q)) {
        return cat;
      }
      const faqs = faqMatchesSearch(cat, q);
      return faqs.length ? { ...cat, faqs } : null;
    })
    .filter((cat): cat is FAQCategory => cat !== null);
};

interface CategoryNavProps {
  categories: typeof faqCategories;
  activeId: string;
  onSelect: (id: string) => void;
}

const CategoryNav = ({ categories, activeId, onSelect }: CategoryNavProps) => (
  <nav aria-label="FAQ categories" className="rounded-xl border border-border bg-card p-3">
    <p className="px-3 pb-2 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      Categories
    </p>
    <ul className="space-y-1">
      {categories.map((cat) => {
        const Icon = iconMap[cat.icon as keyof typeof iconMap] ?? HelpCircle;
        const isActive = cat.id === activeId;
        return (
          <li key={cat.id}>
            <button
              type="button"
              onClick={() => onSelect(cat.id)}
              className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary"
              }`}
            >
              <span className="flex items-center gap-2">
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{cat.title}</span>
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  isActive
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {cat.faqs.length}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  </nav>
);

const Faqs = () => {
  const [activeId, setActiveId] = useState(faqCategories[0].id);
  const [query, setQuery] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const activeCategory = useMemo(
    () => faqCategories.find((c) => c.id === activeId) ?? faqCategories[0],
    [activeId],
  );

  const filteredCategories = useMemo(() => filterFaqCategories(query), [query]);

  useEffect(() => {
    if (!filteredCategories.find((c) => c.id === activeId) && filteredCategories[0]) {
      setActiveId(filteredCategories[0].id);
    }
  }, [filteredCategories, activeId]);

  const displayCategory =
    filteredCategories.find((c) => c.id === activeId) ?? filteredCategories[0] ?? activeCategory;

  const visibleCategories = filteredCategories.length ? filteredCategories : faqCategories;

  const handleCategorySelect = (id: string) => {
    setActiveId(id);
    setMobileNavOpen(false);
  };

  return (
    <div className="daily-commute-page bg-background text-foreground">
      <section className="bg-hero-overlay px-4 py-10 sm:py-16 lg:py-20">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3 py-1.5 text-xs font-medium text-primary-foreground sm:text-sm">
            <HelpCircle className="h-4 w-4" /> Duri Help Center
          </div>
          <h1 className="mb-4 text-2xl font-extrabold leading-tight text-primary-foreground sm:text-4xl lg:text-5xl">
            Frequently Asked Questions
          </h1>
          <p className="mx-auto mb-5 max-w-2xl text-sm text-primary-foreground/80 sm:text-base lg:text-lg">
            Everything you need to know about Duri — verified ridesharing, safety, fares, privacy
            and city availability.
          </p>

          <div className="mx-auto mb-6 flex max-w-2xl flex-wrap items-center justify-center gap-2 text-xs sm:text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/10 px-3 py-1 text-primary-foreground">
              <Globe className="h-3.5 w-3.5" /> Works worldwide
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground px-3 py-1 font-semibold text-primary">
              🇮🇳 India · Active
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/90 px-3 py-1 font-semibold text-primary">
              🇺🇸 USA · Beta
            </span>
          </div>

          <div className="mx-auto flex max-w-xl items-center gap-2 rounded-xl bg-background p-2 shadow-lg">
            <Search className="ml-2 h-5 w-5 shrink-0 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search FAQs... (SOS, fare, USA, Aadhaar)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="border-0 focus-visible:ring-0"
              aria-label="Search FAQs"
            />
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 pt-4 lg:hidden">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Menu className="h-4 w-4" />
                Browse Categories
              </span>
              <span className="text-xs text-muted-foreground">
                {displayCategory?.title}
              </span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto">
            <SheetHeader>
              <SheetTitle>FAQ Categories</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <CategoryNav
                categories={visibleCategories}
                activeId={displayCategory?.id ?? activeId}
                onSelect={handleCategorySelect}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <main className="container mx-auto px-4 py-8 sm:py-12">
        <div className="grid gap-6 lg:grid-cols-[280px_1fr] lg:gap-8">
          <aside className="hidden lg:sticky lg:top-20 lg:block lg:self-start">
            <CategoryNav
              categories={visibleCategories}
              activeId={displayCategory?.id ?? activeId}
              onSelect={handleCategorySelect}
            />
          </aside>

          <section className="min-w-0">
            {displayCategory ? (
              <article>
                <header className="mb-5 border-b border-border pb-4 sm:mb-6">
                  <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <span>FAQs</span>
                    <ChevronRight className="h-3 w-3" />
                    <span className="text-foreground">{displayCategory.title}</span>
                  </div>
                  <h2 className="text-xl font-bold text-foreground sm:text-2xl lg:text-3xl">
                    {displayCategory.title}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                    {displayCategory.description}
                  </p>
                </header>

                <Accordion type="single" collapsible className="w-full">
                  {displayCategory.faqs.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id} id={faq.id}>
                      <AccordionTrigger className="text-left text-sm font-semibold sm:text-base">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
                        {faq.visual && (
                          <figure
                            className={`relative overflow-hidden rounded-xl border border-border bg-gradient-to-br ${faq.visual.gradient} p-4 sm:p-5`}
                            aria-hidden="true"
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-background/80 text-3xl shadow-sm backdrop-blur sm:h-16 sm:w-16 sm:text-4xl">
                                {faq.visual.emoji}
                              </div>
                              <figcaption className="text-sm font-semibold text-foreground sm:text-base">
                                {faq.visual.caption}
                              </figcaption>
                            </div>
                            <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-background/30 blur-2xl" />
                            <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-background/20 blur-2xl" />
                          </figure>
                        )}
                        <p>{faq.answer}</p>

                        {faq.navigation && faq.navigation.length > 0 && (
                          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 sm:p-4">
                            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
                              <MapPin className="h-3.5 w-3.5" /> In-App Navigation
                            </p>
                            <ol
                              className="flex flex-wrap items-center gap-x-1 gap-y-1.5 text-xs sm:text-sm"
                              aria-label="Navigation steps"
                            >
                              {faq.navigation.map((step, idx) => (
                                <li key={idx} className="flex items-center gap-1">
                                  <span
                                    className={`rounded-md px-2 py-1 font-medium ${
                                      idx === faq.navigation!.length - 1
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-background text-foreground border border-border"
                                    }`}
                                  >
                                    {step}
                                  </span>
                                  {idx < faq.navigation!.length - 1 && (
                                    <ChevronRight
                                      className="h-3.5 w-3.5 text-muted-foreground"
                                      aria-hidden="true"
                                    />
                                  )}
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                <FAQForum categoryId={displayCategory.id} categoryTitle={displayCategory.title} />
              </article>
            ) : (
              <p className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
                No questions match your search. Try a different keyword.
              </p>
            )}
          </section>
        </div>

        <section className="mt-12 rounded-2xl bg-secondary/40 p-6 text-center sm:mt-16 sm:p-8">
          <h2 className="mb-3 text-xl font-bold text-foreground sm:text-2xl">
            Still have questions?
          </h2>
          <p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground sm:text-base">
            Our Telugu and English support team is available via in-app chat, WhatsApp and phone.
            Download Duri to chat with us instantly or browse live ride availability before you sign up.
          </p>
          <section className="rounded-2xl bg-guest-gradient p-8 sm:p-12">
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-foreground/20">
                <Smartphone className="h-7 w-7 text-primary-foreground" />
              </div>
              <h3 className="text-2xl font-bold text-primary-foreground sm:text-3xl">
                Looking for a Ride? Download Duri!
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.hst.duriapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg bg-primary-foreground px-5 py-3 text-sm font-semibold text-foreground transition-opacity hover:opacity-90"
                >
                  Google Play <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="https://apps.apple.com/in/app/duri/id6755706995"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg border-2 border-primary-foreground/30 px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  App Store <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </section>
        </section>
      </main>
    </div>
  );
};

export default Faqs;
